import { Button } from '../../../ui/button';
import { Mail, Power } from 'lucide-react';
import type { Facility } from '../../utils/utils';
import { getDaysRemaining, isFacilityDeactivated } from '../../utils/utils';
import FacilityIdentityCell from '../cells/FacilityIdentityCell';
import AdminStatusCell from '../cells/AdminStatusCell';
import DeactivatedBadge from '../cells/DeactivatedBadge';
import LicenseCell from '../cells/LicenseCell';
import type {
    FacilityColumn,
    StageFilterOption,
    StageSortOption,
} from '../utils/facilityTableTypes';

interface CreateColumnsOptions {
    onInviteAdmin?: (facility: Facility) => void;
    onDeactivate?: (facility: Facility) => void;
}

export const onboardingFilterOptions: StageFilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'go-live-soon', label: 'Trial start this week' },
    {
        value: 'pending-site-admin-invitation',
        label: 'Pending Site Admin Invitation',
    },
    { value: 'deactivated', label: 'Deactivated' },
];

export const onboardingSortOptions: StageSortOption[] = [];

export function createOnboardingColumns({
    onInviteAdmin,
    onDeactivate,
}: CreateColumnsOptions): FacilityColumn[] {
    return [
        {
            key: 'facility',
            header: 'Facility',
            className: 'w-[22%]',
            cell: (f) => <FacilityIdentityCell facility={f} />,
        },
        {
            key: 'go-live',
            header: 'Trial start',
            className: 'w-[18%]',
            cell: (f) => <GoLiveCell facility={f} />,
        },
        {
            key: 'admin',
            header: 'Site Admin',
            className: 'w-[16%]',
            cell: (f) => <AdminStatusCell facility={f} />,
        },
        {
            key: 'licenses',
            header: 'Licenses',
            className: 'w-[12%]',
            cell: (f) => <LicenseCell facility={f} />,
        },
        {
            key: 'readiness',
            header: 'Readiness',
            className: 'w-[10%]',
            cell: (f) => <ReadinessCell score={f.readinessScore} />,
        },
        {
            key: 'actions',
            header: '',
            className: 'w-[22%] text-right',
            headerClassName: 'text-right',
            cell: (f) => (
                <div className="flex items-center justify-end gap-2">
                    {isFacilityDeactivated(f) ? (
                        <DeactivatedBadge />
                    ) : (
                        <>
                            {!f.admin && onInviteAdmin && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onInviteAdmin(f);
                                    }}
                                >
                                    <Mail
                                        className="mr-1.5 h-3 w-3"
                                        aria-hidden="true"
                                    />
                                    Invite
                                </Button>
                            )}
                            {onDeactivate && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeactivate(f);
                                    }}
                                >
                                    <Power
                                        className="mr-1 h-3 w-3"
                                        aria-hidden="true"
                                    />
                                    Deactivate
                                </Button>
                            )}
                        </>
                    )}
                </div>
            ),
        },
    ];
}

function GoLiveCell({ facility }: { facility: Facility }) {
    const deactivated = isFacilityDeactivated(facility);
    const dateLabel = facility.start_date
        ? new Date(facility.start_date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : 'Not set';

    if (deactivated) {
        return <span className="text-xs text-muted-foreground">—</span>;
    }

    const days = getDaysRemaining(facility.start_date, 'go-live');

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground">{dateLabel}</span>
            <GoLiveBadge days={days} />
        </div>
    );
}

function GoLiveBadge({ days }: { days: number | null }) {
    if (days === null) {
        return (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Not set
            </span>
        );
    }

    const label =
        days < 0
            ? `${Math.abs(days)}d overdue`
            : days === 0
              ? 'Today'
              : `${days}d left`;
    const className =
        days <= 7
            ? 'border-amber-300/15 bg-amber-300/5 text-amber-200'
            : 'border-white/10 bg-white/5 text-muted-foreground';

    return (
        <span
            className={`rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums ${className}`}
        >
            {label}
        </span>
    );
}

function ReadinessCell({ score }: { score?: number }) {
    if (score == null) {
        return <span className="text-xs text-muted-foreground/50">—</span>;
    }

    return (
        <div className="min-w-20">
            <span className="text-xs font-medium tabular-nums text-foreground">
                {score}%
            </span>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/5">
                <div
                    className="h-full rounded-full bg-zinc-300"
                    style={{ width: `${Math.min(score, 100)}%` }}
                />
            </div>
        </div>
    );
}
